import React, { useState, useCallback } from "react";
import Input from "../../../components/Input/Input";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  Tooltip,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  InfoOutlined as InfoOutlinedIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { openPopup } from "../../../stateManagement/actions/popupActions";
import { TemplateReportService } from "../../../services/templateReportService";
import {
  COLUMN_TYPE_OPTIONS,
  FILTER_TYPE_OPTIONS,
  PLACEHOLDERS,
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
  UI_CONSTANTS,
} from "./constants";
import "./TemplateReports.css";

// TypeScript interfaces for type safety
export interface ColumnMetadata {
  label: string;
  type: string;
  default?: boolean;
}

export interface FilterConfig {
  id: string;
  label: string;
  filter: 'daterange' | 'periodfilter' | 'monthyear' | 'daterangeAndPeriod' | null;
}

export interface GlobalFilterConfig {
  id: string;
  label: string;
  api: string;
  parent?: string;
  multiSelect: boolean;
}

export interface TemplateOption {
  id: string;
  name: string;
  type: string;
  role: string;
  filter: any;
  globalFilters: GlobalFilterConfig[] | null;
  query?: string;
  default_columns?: string;
  all_columns?: Record<string, ColumnMetadata>;
  reportTemplateConfig?: Record<string, string>;
}

interface TemplateAdminProps {
  template: TemplateOption;
  onTemplateUpdated: () => void;
  onClose: () => void;
  isCreateMode?: boolean;
}

const DEFAULT_REPORT_TEMPLATE_CONFIG = {
  fetchLimit: "10000",
  language: "sql",
  format: "csv",
  reportDelimiter: ",",
  implementation: "com.applicate.services.channelkart.reports.CustomTemplateReport",
  designations: "admin",
  baseQuery: ""
};

const getInitialReportTemplateConfig = (template: TemplateOption) => {
  if (template && template.reportTemplateConfig) {
    // Extract known keys
    const config: any = {};
    const extra: { key: string; value: string }[] = [];
    Object.entries(template.reportTemplateConfig).forEach(([key, value]) => {
      if (key in DEFAULT_REPORT_TEMPLATE_CONFIG) {
        config[key] = value;
      } else {
        extra.push({ key, value: String(value) });
      }
    });
    return { config: { ...DEFAULT_REPORT_TEMPLATE_CONFIG, ...config }, extra };
  }
  return { config: { ...DEFAULT_REPORT_TEMPLATE_CONFIG }, extra: [] };
};

const TemplateAdmin: React.FC<TemplateAdminProps> = ({
  template,
  onTemplateUpdated,
  onClose,
  isCreateMode = false,
}) => {
  const [templateName, setTemplateName] = useState<string>(template.name);
  const [templateColumns, setTemplateColumns] = useState<Record<string, ColumnMetadata>>(template.all_columns || {});
  const [templateFilters, setTemplateFilters] = useState<FilterConfig[]>(template.filter ? [{
    id: "filter",
    label: "Filter", 
    filter: template.filter as 'daterange' | 'periodfilter' | 'monthyear' | 'daterangeAndPeriod' | null
  }] : []);
  const [templateGlobalFilters, setTemplateGlobalFilters] = useState<GlobalFilterConfig[]>(template.globalFilters || []);
  const [newColumnKey, setNewColumnKey] = useState<string>("");
  const [newColumnLabel, setNewColumnLabel] = useState<string>("");
  const [newColumnType, setNewColumnType] = useState<string>("text");
  const [newGlobalFilterId, setNewGlobalFilterId] = useState<string>("");
  const [newGlobalFilterLabel, setNewGlobalFilterLabel] = useState<string>("");
  const [newGlobalFilterApi, setNewGlobalFilterApi] = useState<string>("");
  const [newGlobalFilterParent, setNewGlobalFilterParent] = useState<string>("");
  const [newColumnDefault, setNewColumnDefault] = useState<boolean>(false);
  const [columnFeedback, setColumnFeedback] = useState<{ open: boolean; message: string; type: 'success' | 'error' }>({ open: false, message: '', type: 'success' });
  const [newGlobalFilterMultiSelect, setNewGlobalFilterMultiSelect] = useState<boolean>(false);

  // --- Report Template Config State ---
  const initialConfig = getInitialReportTemplateConfig(template);
  const [reportTemplateConfig, setReportTemplateConfig] = useState<Record<string, string>>(initialConfig.config);
  const [extraConfigProps, setExtraConfigProps] = useState<{ key: string; value: string }[]>(initialConfig.extra);

  const [advancedOptionsOpen, setAdvancedOptionsOpen] = useState<boolean>(false);
  const [editGlobalFilterIndex, setEditGlobalFilterIndex] = useState<number | null>(null);

  const handleSaveTemplate = async () => {
    try {
      // Merge extraConfigProps into reportTemplateConfig
      const mergedReportTemplateConfig = {
        ...reportTemplateConfig,
        ...extraConfigProps.reduce((acc, prop) => {
          if (prop.key) acc[prop.key] = prop.value;
          return acc;
        }, {} as Record<string, string>)
      };

      const templatePayload = {
        templateName: templateName,
        filter: templateFilters.length > 0 ? templateFilters[0].filter : null,
        globalFilters: templateGlobalFilters.length > 0 ? templateGlobalFilters : null,
        allColumns: templateColumns,
        reportTemplateConfig: mergedReportTemplateConfig
      };

      if (isCreateMode) {
        await TemplateReportService.createTemplate(templatePayload);
        openPopup("Success", SUCCESS_MESSAGES.TEMPLATE_CREATED || "Template created successfully");
      } else {
        await TemplateReportService.updateTemplate(template.name, templatePayload);
        openPopup("Success", SUCCESS_MESSAGES.TEMPLATE_UPDATED);
      }
      onTemplateUpdated();
      onClose();
    } catch (error) {
      openPopup("Error", isCreateMode ? ERROR_MESSAGES.CREATE_TEMPLATE : ERROR_MESSAGES.UPDATE_TEMPLATE);
    }
  };

  const handleAddColumn = () => {
    if (!newColumnKey || !newColumnLabel) return;

    setTemplateColumns(prev => ({
      ...prev,
      [newColumnKey]: {
        label: newColumnLabel,
        type: newColumnType,
        ...(newColumnDefault ? { default: true } : {})
      }
    }));

    setNewColumnKey("");
    setNewColumnLabel("");
    setNewColumnType("text");
    setNewColumnDefault(false);
    setColumnFeedback({ open: true, message: 'Column added', type: 'success' });
  };

  const handleRemoveColumn = (columnKey: string) => {
    setTemplateColumns(prev => {
      const newColumns = { ...prev };
      delete newColumns[columnKey];
      return newColumns;
    });
    setColumnFeedback({ open: true, message: 'Column removed', type: 'success' });
  };

  const handleToggleColumnDefault = (columnKey: string) => {
    setTemplateColumns(prev => {
      const newColumns = { ...prev };
      newColumns[columnKey] = {
        ...newColumns[columnKey],
        default: !newColumns[columnKey]?.default
      };
      return newColumns;
    });
    setColumnFeedback({ open: true, message: 'Default status updated', type: 'success' });
  };

  const handleAddOrUpdateGlobalFilter = () => {
    if (!newGlobalFilterId || !newGlobalFilterLabel || !newGlobalFilterApi) return;
    const newFilter: GlobalFilterConfig = {
      id: newGlobalFilterId,
      label: newGlobalFilterLabel,
      api: newGlobalFilterApi,
      parent: newGlobalFilterParent || "",
      multiSelect: newGlobalFilterMultiSelect
    };
    setTemplateGlobalFilters(prev => {
      if (editGlobalFilterIndex !== null) {
        const updated = [...prev];
        updated[editGlobalFilterIndex] = newFilter;
        return updated;
      }
      return [...prev, newFilter];
    });
    setNewGlobalFilterId("");
    setNewGlobalFilterLabel("");
    setNewGlobalFilterApi("");
    setNewGlobalFilterParent("");
    setNewGlobalFilterMultiSelect(false);
    setEditGlobalFilterIndex(null);
  };

  const handleEditGlobalFilter = (index: number) => {
    const filter = templateGlobalFilters[index];
    setNewGlobalFilterId(filter.id);
    setNewGlobalFilterLabel(filter.label);
    setNewGlobalFilterApi(filter.api);
    setNewGlobalFilterParent(filter.parent || "");
    setNewGlobalFilterMultiSelect(!!filter.multiSelect);
    setEditGlobalFilterIndex(index);
  };

  const handleRemoveGlobalFilter = (filterId: string) => {
    setTemplateGlobalFilters(prev => prev.filter(f => f.id !== filterId));
  };

  return (
    <Dialog
      open={true}
      maxWidth="md"
      fullWidth
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') onClose();
      }}
    >
      <DialogTitle>
        <Typography variant="h6" color={UI_CONSTANTS.PRIMARY_COLOR}>
          {isCreateMode ? 'Create New Template' : `Edit Template: ${template.name}`}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <div className="edit-template-form">
          {/* Basic Info Section */}
          <div className="form-section" style={{ marginBottom: 24 }}>
            <Typography variant="subtitle1" gutterBottom style={{ fontWeight: 600, marginBottom: 16 }}>
              Basic Info
            </Typography>
            <Input
              type="text"
              label="Template Name"
              value={templateName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTemplateName(e.target.value)}
              className="template-name-input"
              placeholder={PLACEHOLDERS.TEMPLATE_NAME}
              variant="outlined"
              fullWidth
              style={{ fontWeight: 600, fontSize: 18 }}
            />
          </div>

          {/* Report Template Config Section */}
          <div className="form-section" style={{ marginBottom: 24 }}>
            <Typography variant="subtitle1" gutterBottom style={{ fontWeight: 600, marginBottom: 16 }}>
              Report Template Config
            </Typography>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <Input
                type="text"
                label="Fetch Limit"
                value={reportTemplateConfig.fetchLimit}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setReportTemplateConfig((cfg: Record<string, string>) => ({ ...cfg, fetchLimit: e.target.value }))}
                variant="outlined"
                fullWidth
              />
              <Input
                type="text"
                label="Language"
                value={reportTemplateConfig.language}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setReportTemplateConfig((cfg: Record<string, string>) => ({ ...cfg, language: e.target.value }))}
                variant="outlined"
                fullWidth
              />
              <Input
                type="text"
                label="Format"
                value={reportTemplateConfig.format}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setReportTemplateConfig((cfg: Record<string, string>) => ({ ...cfg, format: e.target.value }))}
                variant="outlined"
                fullWidth
              />
              <Input
                type="text"
                label="Report Delimiter"
                value={reportTemplateConfig.reportDelimiter}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setReportTemplateConfig((cfg: Record<string, string>) => ({ ...cfg, reportDelimiter: e.target.value }))}
                variant="outlined"
                fullWidth
              />
              <Input
                type="text"
                label="Implementation"
                value={reportTemplateConfig.implementation}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setReportTemplateConfig((cfg: Record<string, string>) => ({ ...cfg, implementation: e.target.value }))}
                variant="outlined"
                fullWidth
              />
              <Input
                type="text"
                label="Designations"
                value={reportTemplateConfig.designations}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setReportTemplateConfig((cfg: Record<string, string>) => ({ ...cfg, designations: e.target.value }))}
                variant="outlined"
                fullWidth
              />
              <Input
                type="text"
                label="Base Query (optional)"
                value={reportTemplateConfig.baseQuery}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setReportTemplateConfig((cfg: Record<string, string>) => ({ ...cfg, baseQuery: e.target.value }))}
                variant="outlined"
                fullWidth
                multiline
                rows={2}
              />
            </div>
            {/* Dynamic extra properties - improved UI */}
            <div style={{ marginTop: 32 }}>
              <hr style={{ margin: '24px 0', border: 0, borderTop: '1px solid #e2e8f0' }} />
              <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 16 }}>
                Extra Properties
              </Typography>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {extraConfigProps.length === 0 && (
                  <Typography color="textSecondary" style={{ textAlign: 'center', marginBottom: 8 }}>
                    No extra properties added yet.
                  </Typography>
                )}
                {extraConfigProps.map((prop, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: 12, alignItems: 'center', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 12 }}>
                    <Input
                      type="text"
                      label="Key"
                      value={prop.key}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const newProps = [...extraConfigProps];
                        newProps[idx].key = e.target.value;
                        setExtraConfigProps(newProps);
                      }}
                      variant="outlined"
                      fullWidth
                    />
                    <Input
                      type="text"
                      label="Value"
                      value={prop.value}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const newProps = [...extraConfigProps];
                        newProps[idx].value = e.target.value;
                        setExtraConfigProps(newProps);
                      }}
                      variant="outlined"
                      fullWidth
                    />
                    <IconButton color="error" onClick={() => setExtraConfigProps(props => props.filter((_, i) => i !== idx))}>
                      <DeleteIcon />
                    </IconButton>
                  </div>
                ))}
                <Button
                  variant="contained"
                  sx={{ backgroundColor: '#24c6b1', color: '#fff', '&:hover': { backgroundColor: '#189e91' }, alignSelf: 'center', marginTop: 1, minWidth: 180, fontWeight: 600, fontSize: 15 }}
                  onClick={() => setExtraConfigProps(props => [...props, { key: '', value: '' }])}
                  aria-label="Add Extra Property"
                >
                  + Add Extra Property
                </Button>
              </div>
            </div>
          </div>

          {/* Columns Section */}
          <div className="form-section" style={{ marginBottom: 24 }}>
            <Typography variant="subtitle1" gutterBottom style={{ fontWeight: 600, marginBottom: 16 }}>
              Columns
            </Typography>
            <Paper elevation={2} style={{ padding: 20, marginBottom: 24, background: '#f8fafc' }}>
              <Typography variant="subtitle2" style={{ fontWeight: 500, marginBottom: 12 }}>
                Add New Column
              </Typography>
              <div className="add-filter-section" style={{ marginBottom: 0, background: 'transparent', border: 'none', padding: 0, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 0.5fr 0.5fr auto', gap: 16 }}>
                <Input
                  type="text"
                  label="Column Key"
                  value={newColumnKey}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewColumnKey(e.target.value)}
                  placeholder={PLACEHOLDERS.COLUMN_KEY}
                  variant="outlined"
                  fullWidth
                />
                <Input
                  type="text"
                  label="Column Label"
                  value={newColumnLabel}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewColumnLabel(e.target.value)}
                  placeholder={PLACEHOLDERS.COLUMN_LABEL}
                  variant="outlined"
                  fullWidth
                />
                <FormControl size="small" className="parent-filter-select" variant="outlined" fullWidth>
                  <InputLabel>Column Type</InputLabel>
                  <Select
                    value={newColumnType}
                    onChange={(e) => setNewColumnType(e.target.value)}
                    label="Column Type"
                  >
                    {COLUMN_TYPE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" style={{ alignSelf: 'center', gridColumn: 'span 2', marginTop: -20, background: '#f1f5f9', borderRadius: 8, padding: '4px 8px', display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 5 }}>
                  <Switch
                    checked={newColumnDefault}
                    onChange={e => setNewColumnDefault(e.target.checked)}
                    color="primary"
                    inputProps={{ 'aria-label': 'default column' }}
                  />
                  <span style={{ fontWeight: 600, fontSize: 15, color: '#1e293b'}}>
                    default
                  </span>
                  <Tooltip title="Default columns are shown by default in the report. You can always change this later.">
                    <InfoOutlinedIcon fontSize="small" style={{ color: '#64748b', verticalAlign: 'middle' }} />
                  </Tooltip>
                </FormControl>
                <Button
                  variant="contained"
                  sx={{ backgroundColor: '#24c6b1', color: '#fff', '&:hover': { backgroundColor: '#189e91' } }}
                  startIcon={<AddIcon />}
                  onClick={handleAddColumn}
                  disabled={!newColumnKey || !newColumnLabel}
                  style={{ minWidth: 120, height: 40 ,marginTop: 15}}
                  aria-label="Add Column"
                >
                  Add
                </Button>
              </div>
            </Paper>
            <TableContainer component={Paper} elevation={1} style={{ background: '#fff' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell style={{ fontWeight: 600 }}>Key</TableCell>
                    <TableCell style={{ fontWeight: 600 }}>Label</TableCell>
                    <TableCell style={{ fontWeight: 600 }}>Type</TableCell>
                    <TableCell style={{ fontWeight: 600 }}>Default</TableCell>
                    <TableCell style={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(templateColumns).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography color="textSecondary">No columns added yet.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    Object.entries(templateColumns).map(([columnKey, columnMeta]) => (
                      <TableRow key={columnKey}>
                        <TableCell>{columnKey}</TableCell>
                        <TableCell>{columnMeta.label}</TableCell>
                        <TableCell>{columnMeta.type}</TableCell>
                        <TableCell>
                          <Tooltip title="Toggle default column">
                            <Switch
                              checked={!!columnMeta.default}
                              onChange={() => handleToggleColumnDefault(columnKey)}
                              color="primary"
                              inputProps={{ 'aria-label': 'Toggle default' }}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Remove column">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveColumn(columnKey)}
                              className="remove-column-icon"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Snackbar
              open={columnFeedback.open}
              autoHideDuration={2000}
              onClose={() => setColumnFeedback({ ...columnFeedback, open: false })}
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
              <Alert severity={columnFeedback.type} sx={{ width: '100%' }}>
                {columnFeedback.message}
              </Alert>
            </Snackbar>
          </div>

          {/* Filters Section - Only one filter allowed */}
          <div className="form-section" style={{ marginBottom: 24 }}>
            <Typography variant="subtitle1" gutterBottom style={{ fontWeight: 600, marginBottom: 16 }}>
              Filter (Only one allowed)
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
              Select a filter type to be shown in the report download. If no filter is selected, no filter will be displayed.
            </Typography>
            <div className="add-filter-section" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16 }}>
              <FormControl size="small" className="parent-filter-select" variant="outlined" fullWidth>
                <InputLabel>Filter Type</InputLabel>
                <Select
                  value={templateFilters.length > 0 ? (templateFilters[0].filter === null ? "null" : templateFilters[0].filter) : "null"}
                  onChange={(e) => {
                    const value = e.target.value === "null" ? null : e.target.value;
                    setTemplateFilters([{
                      id: "filter",
                      label: "Filter",
                      filter: value as 'daterange' | 'periodfilter' | 'monthyear' | 'daterangeAndPeriod' | null
                    }]);
                  }}
                  label="Filter Type"
                >
                  {FILTER_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option.value === null ? "null" : option.value} value={option.value === null ? "null" : option.value}>
                      <Tooltip title={
                        option.value === 'daterange' ? 'Select a date range (from - to)' :
                        option.value === 'periodfilter' ? 'Select a specific period' :
                        option.value === 'monthyear' ? 'Select month and year' :
                        option.value === 'daterangeAndPeriod' ? 'Select both date range and period' :
                        option.value === null ? 'No filter will be shown in the report' :
                        option.label
                      }>
                        <span>{option.label}</span>
                      </Tooltip>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>

          {/* Global Filters Section */}
          <div className="form-section" style={{ marginBottom: 24 }}>
            <Typography variant="subtitle1" gutterBottom style={{ fontWeight: 600, marginBottom: 16 }}>
              Global Filters
            </Typography>
            <div className="add-filter-section" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr auto', gap: 16, marginBottom: 16 }}>
              <Input
                type="text"
                label="Filter ID"
                value={newGlobalFilterId}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewGlobalFilterId(e.target.value)}
                placeholder={PLACEHOLDERS.GLOBAL_FILTER_ID}
                variant="outlined"
                fullWidth
                helperText="Unique identifier for the filter (required)"
                required
              />
              <Input
                type="text"
                label="Filter Label"
                value={newGlobalFilterLabel}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewGlobalFilterLabel(e.target.value)}
                placeholder={PLACEHOLDERS.GLOBAL_FILTER_LABEL}
                variant="outlined"
                fullWidth
                helperText="Display name for the filter (required)"
                required
              />
              <Input
                type="text"
                label="API Endpoint"
                value={newGlobalFilterApi}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewGlobalFilterApi(e.target.value)}
                placeholder={PLACEHOLDERS.GLOBAL_FILTER_API}
                variant="outlined"
                fullWidth
                helperText="API endpoint for fetching filter options (required)"
                required
              />
              <FormControl size="small" className="parent-filter-select" variant="outlined" fullWidth>
                <InputLabel>Parent Filter</InputLabel>
                <Select
                  value={newGlobalFilterParent}
                  onChange={(e) => setNewGlobalFilterParent(e.target.value)}
                  label="Parent Filter"
                >
                  <MenuItem value="">None</MenuItem>
                  {templateGlobalFilters.map((filter) => (
                    <MenuItem key={filter.id} value={filter.id}>
                      {filter.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" style={{ alignSelf: 'center', minWidth: 120, display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 5 }}>
                <Switch
                  checked={newGlobalFilterMultiSelect}
                  onChange={e => setNewGlobalFilterMultiSelect(e.target.checked)}
                  color="primary"
                  inputProps={{ 'aria-label': 'multiSelect' }}
                />
                <span style={{ fontWeight: 600, fontSize: 15, color: '#1e293b'}}>
                  MultiSelect
                </span>
              </FormControl>
              <Button
                variant="contained"
                sx={{ backgroundColor: '#24c6b1', color: '#fff', '&:hover': { backgroundColor: '#189e91' } }}
                startIcon={editGlobalFilterIndex !== null ? <EditIcon /> : <AddIcon />}
                onClick={handleAddOrUpdateGlobalFilter}
                disabled={!newGlobalFilterId || !newGlobalFilterLabel || !newGlobalFilterApi}
                aria-label={editGlobalFilterIndex !== null ? "Update Global Filter" : "Add Global Filter"}
                className="global-filter-btn"
              >
                {editGlobalFilterIndex !== null ? "Update" : "Add"} Global Filter
              </Button>
            </div>
            <div className="filters-list">
              {templateGlobalFilters.map((filter, idx) => (
                <div key={filter.id} className="filter-item" style={{ marginBottom: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.04)', border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 16 }}>
                  <div className="filter-info">
                    <Typography variant="body2" className="filter-id" style={{ fontWeight: 500 }}>
                      {filter.id}
                    </Typography>
                    <Typography variant="body2" className="filter-label" style={{ color: '#64748b' }}>
                      {filter.label}
                    </Typography>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
                      <span style={{ background: '#e0f7fa', color: '#00796b', borderRadius: 4, padding: '2px 8px', fontSize: 12 }}>API: {filter.api}</span>
                      <span style={{ background: '#ede7f6', color: '#512da8', borderRadius: 4, padding: '2px 8px', fontSize: 12 }}>
                        Parent: {filter.parent || 'None'}
                      </span>
                      <span style={{ background: filter.multiSelect ? '#f1f8e9' : '#ffebee', color: filter.multiSelect ? '#33691e' : '#b71c1c', borderRadius: 4, padding: '2px 8px', fontSize: 12 }}>
                        MultiSelect: {filter.multiSelect ? 'Yes' : 'No'}
                      </span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleEditGlobalFilter(idx)}
                      style={{ transition: 'background 0.2s', borderRadius: 6 }}
                      aria-label="Edit Global Filter"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveGlobalFilter(filter.id)}
                      style={{ transition: 'background 0.2s', borderRadius: 6 }}
                      aria-label="Delete Global Filter"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined" aria-label="Cancel">Cancel</Button>
        <Button
          onClick={handleSaveTemplate}
          variant="contained"
          sx={{ backgroundColor: '#24c6b1', color: '#fff', '&:hover': { backgroundColor: '#189e91' } }}
          aria-label={isCreateMode ? 'Create Template' : 'Update Template'}
        >
          {isCreateMode ? 'Create Template' : 'Update Template'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TemplateAdmin; 